import { Component, ViewChild, signal, OnInit, OnDestroy, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, RouterModule, Router, NavigationEnd } from '@angular/router';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subject, takeUntil, filter, Subscription, firstValueFrom, forkJoin } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { HeaderComponent } from './header/header.component';
import { FooterComponent } from './footer/footer.component';
import { LoginService } from '../auth/login.service';
import { isPortalPushConfigured, syncPortalFcmPush } from '../shared/push/portal-push';
import { PortalNotificationHubService } from '../shared/services/portal-notification-hub.service';
import { isPortalPublicAuthRoute } from '../shared/utils/recovery-flow.utils';
import { WentyComponent } from '../wenty/wenty.component';
import { BirthdayCelebrationComponent } from '../shared/birthday-celebration/birthday-celebration.component';
import { shouldShowBirthdayCelebration } from '../shared/birthday-celebration/birthday-celebration.utils';

interface BreadcrumbItem {
  label: string;
  route: string;
  isHome?: boolean;
}

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CdkScrollable,
    CommonModule,
    RouterOutlet,
    RouterModule,
    MatSidenavModule,
    MatToolbarModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatTooltipModule,
    MatBadgeModule,
    HeaderComponent,
    FooterComponent,
    WentyComponent,
    BirthdayCelebrationComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer!: MatSidenav;

  // Signals para estado reactivo
  isSidenavExpanded = signal(false);
  currentRoute = signal('');
  isHandset = signal(false);
  /** Birthday overlay — FE-only, once per year (see birthday-celebration.utils). */
  showBirthdayCelebration = signal(false);
  /** Tracks ngx-translate so breadcrumb/title computeds refresh on language change. */
  private uiLang = signal('es');

  // Computed signals
  sidenavWidth = computed(() => (this.isSidenavExpanded() ? 280 : 72));
  currentPageTitle = computed(() => {
    this.uiLang();
    return this.getPageTitleFromRoute(this.currentRoute());
  });
  breadcrumbs = computed(() => {
    this.uiLang();
    return this.generateBreadcrumbs(this.currentRoute());
  });
  showFooter = computed(() => {
    const currentUrl = this.currentRoute();
    if (!this.loginService.isLoggedIn()) return false;
    return !isPortalPublicAuthRoute(currentUrl);
  });

  showHeader = computed(() => {
    const currentUrl = this.currentRoute();
    if (!this.loginService.isLoggedIn()) return false;
    if (isPortalPublicAuthRoute(currentUrl)) return false;
    if (currentUrl.split('/')[1] === 'onboarding') return false;
    return true;
  });

  /** FAB: authenticated + not public auth route + wenty:use. */
  showWenty = computed(() => {
    const currentUrl = this.currentRoute();
    if (!this.loginService.isLoggedIn()) return false;
    if (isPortalPublicAuthRoute(currentUrl)) return false;
    if (currentUrl.split('/')[1] === 'onboarding') return false;
    return this.loginService.canUseWenty();
  });

  private destroy$ = new Subject<void>();
  private authSub: Subscription;
  private routeSub!: Subscription;
  /** Once per layout lifetime (each full page load / login shell). */
  private pushSyncStarted = false;

  showSidebar = true;
  sidebarPinned = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private loginService: LoginService,
    private translateService: TranslateService,
    private portalNotifHub: PortalNotificationHubService
  ) {
    this.uiLang.set(this.translateService.getCurrentLang() || 'es');
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(e => {
      this.uiLang.set(e.lang);
    });

    // Effect para observar cambios de breakpoint
    effect(() => {
      this.breakpointObserver
        .observe(Breakpoints.Handset)
        .pipe(takeUntil(this.destroy$))
        .subscribe(result => {
          this.isHandset.set(result.matches);
          if (result.matches) {
            this.isSidenavExpanded.set(false);
          }
        });
    });

    // Effect para observar cambios de ruta
    effect(() => {
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          takeUntil(this.destroy$)
        )
        .subscribe((event: NavigationEnd) => {
          this.currentRoute.set(event.url);
        });
    });

    // Auth state subscription — silent FCM sync once session is live.
    this.authSub = this.loginService.isLoggedIn$().subscribe(isLoggedIn => {
      if (!isLoggedIn) {
        this.pushSyncStarted = false;
        this.showBirthdayCelebration.set(false);
        return;
      }
      if (!isPortalPublicAuthRoute(this.router.url)) {
        this.maybeSyncPushSilent();
        this.maybeShowBirthdayCelebration();
      }
    });

    // Initial check
    // this.updateLayoutVisibility(
    //   this.loginService.isAuthenticated(),
    //   this.router.url
    // );

    // Optionally restore pinned state from localStorage
    const pinned = localStorage.getItem('sidebarPinned');
    this.sidebarPinned = pinned === 'true';
  }

  // updateLayoutVisibility(isLoggedIn: boolean, url: string) {
  //   const unauthRoutes = ['/login', '/forgot-password', '/reset-password'];
  //   const isUnauth = unauthRoutes.includes(url.split('?')[0]);
  //   this.showHeader = isLoggedIn && !isUnauth;
  //   this.showSidebar = isLoggedIn && !isUnauth;
  // }

  ngOnInit() {
    this.currentRoute.set(this.router.url);

    // Belt-and-suspenders: never bootstrap user data while sitting on a
    // public auth route. `LoginService.initializeUserData` re-checks
    // this internally, but doing it here too keeps the intent visible
    // at the call site and avoids even the cheap `fetchCurrentUser`
    // setup work on `/login`, `/forgot-password`, `/reset-password`.
    if (isPortalPublicAuthRoute(this.router.url)) {
      return;
    }

    this.loginService.initializeUserData();
    // Push sync runs from isLoggedIn$ when the session is confirmed.
    // Birthday overlay: re-check after /me may refresh contact.birthday.
    this.loginService
      .getCurrentUser$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.maybeShowBirthdayCelebration());
  }

  onBirthdayCelebrationDismissed(): void {
    this.showBirthdayCelebration.set(false);
  }

  /** Portal FE-only: once on birthday (local calendar), independent of CRM gifts. */
  private maybeShowBirthdayCelebration(): void {
    if (isPortalPublicAuthRoute(this.router.url)) return;
    if (this.router.url.split('/')[1] === 'onboarding') return;
    const user = this.loginService.getUser();
    if (
      !shouldShowBirthdayCelebration({
        userId: user?.id,
        birthday: user?.contact?.birthday ?? user?.birthday,
      })
    ) {
      return;
    }
    this.showBirthdayCelebration.set(true);
  }

  /**
   * On every authenticated visit when master notifications are ON (default):
   * request Notification permission if still `default`, then refresh FCM token
   * + upsert PortalPushSubscription. Silent — Profile button keeps toasts.
   */
  private maybeSyncPushSilent(): void {
    if (this.pushSyncStarted || !isPortalPushConfigured()) return;
    this.pushSyncStarted = true;
    void (async () => {
      try {
        const { prefs, status } = await firstValueFrom(
          forkJoin({
            prefs: this.portalNotifHub.getPrefs(),
            status: this.portalNotifHub.getPushStatus(),
          })
        );
        if (!status.enabled) return;
        // Missing rows = enabled (same as Profile master toggle).
        const masterOn = prefs.length === 0 || prefs.every(p => p.enabled);
        if (!masterOn) return;
        await syncPortalFcmPush(token =>
          firstValueFrom(
            this.portalNotifHub.subscribePush({
              token,
              platform: 'web',
              userAgent: navigator.userAgent,
            })
          )
        );
      } catch {
        // Best-effort — layout must not break on push/FCM failures.
      }
    })();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.authSub) this.authSub.unsubscribe();
    if (this.routeSub) this.routeSub.unsubscribe();
  }

  onSidenavMouseEnter() {
    if (!this.isHandset()) {
      this.isSidenavExpanded.set(true);
    }
  }

  onSidenavMouseLeave() {
    if (!this.isHandset()) {
      this.isSidenavExpanded.set(false);
    }
  }

  toggleSidenav() {
    if (this.isHandset()) {
      this.drawer.toggle();
    }
  }

  onMenuItemSelected(_item: string) {
    if (this.isHandset()) {
      this.drawer.close();
    }
  }

  onBreadcrumbClick(route: string) {
    this.router.navigate([route]);
  }

  private getPageTitleFromRoute(route: string): string {
    const path = route.split('?')[0];
    const t = (key: string) => this.translateService.instant(key);
    if (path.startsWith('/policies')) return t('nav.policies');
    if (path.startsWith('/org-chart')) return t('nav.orgChart');
    const routeMap: Record<string, string> = {
      '/profile': 'nav.profile',
      '/assignments': 'nav.projects',
      '/timesheets': 'nav.timesheets',
      '/coworkers': 'nav.coworkers',
      '/all-hands': 'nav.allHands',
      '/onboarding': 'nav.onboarding',
    };

    return t(routeMap[path] || 'nav.profile');
  }

  private generateBreadcrumbs(route: string): BreadcrumbItem[] {
    const t = (key: string) => this.translateService.instant(key);
    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Ewents', route: '/profile', isHome: true }];
    const path = route.split('?')[0];

    if (path.startsWith('/policies')) {
      const tab = path.split('/')[2];
      const tabLabel: Record<string, string> = {
        policies: 'policies.tabs.policies',
        reviews: 'policies.tabs.reviews',
        training: 'policies.tabs.training',
        'job-descriptions': 'policies.tabs.jobDescriptions',
        whistleblow: 'policies.tabs.whistleblow',
      };
      const items: BreadcrumbItem[] = [{ label: t('nav.policies'), route: '/policies' }];
      if (tab && tabLabel[tab]) {
        items.push({ label: t(tabLabel[tab]), route: `/policies/${tab}` });
      }
      return [...breadcrumbs, ...items];
    }

    if (path.startsWith('/org-chart')) {
      const view = path.split('/')[2];
      const viewLabel: Record<string, string> = {
        chart: 'orgChart.chart',
        map: 'orgChart.map',
      };
      const items: BreadcrumbItem[] = [{ label: t('nav.orgChart'), route: '/org-chart' }];
      if (view && viewLabel[view]) {
        items.push({ label: t(viewLabel[view]), route: `/org-chart/${view}` });
      }
      return [...breadcrumbs, ...items];
    }

    const routeBreadcrumbs: Record<string, BreadcrumbItem[]> = {
      '/profile': [{ label: t('nav.profile'), route: '/profile' }],
      '/assignments': [{ label: t('nav.projects'), route: '/assignments' }],
      '/timesheets': [{ label: t('nav.timesheets'), route: '/timesheets' }],
      '/coworkers': [{ label: t('nav.coworkers'), route: '/coworkers' }],
      '/all-hands': [{ label: t('nav.allHands'), route: '/all-hands' }],
      '/onboarding': [{ label: t('nav.onboarding'), route: '/onboarding' }],
    };

    const routeSegments = routeBreadcrumbs[path] || [];
    return [...breadcrumbs, ...routeSegments];
  }

  onSidebarPinnedChange(pinned: boolean) {
    this.sidebarPinned = pinned;
    localStorage.setItem('sidebarPinned', String(pinned));
  }
}
